import Vue from 'vue';
import {
  getLease as bridgeGetLease,
  setLease as bridgeSetLease,
  removeLease as bridgeRemoveLease,
  buildClaim,
  evaluate as evaluateClaim,
  daysUntilExpiry as daysUntilExpiryFn,
  getDefaultConfig,
  isLeaseFeatureEnabled
} from '@/services/leaseService';

// Módulo Vuex pra leases de download offline. Cada `enrollId` ganha um claim
// {issuedAt, validUntil, graceUntil, revokedAt, studentId, ...} que controla
// se as aulas baixadas podem ser tocadas. State aqui é CACHE pra reatividade
// — fonte de verdade pra segurança é o arquivo nativo assinado HMAC (lido pelo
// scheme handler vxlkey:// antes de devolver a chave AES).
//
// Entitlement (tem acesso ao curso): o backend NÃO emite HTTP 402, então a
// revogação vem da LISTA de enrolls (GET /enrolls) — campos `pendingPayment`,
// `blocked`, `temporarilyDisabled`. `syncEntitlements` (disparado a cada lista
// FRESCA de rede) revoga os bloqueados e renova os liberados. O `touch`
// (qualquer 2xx de /enrolls/) é gateado pelo mapa de bloqueados pra não
// ressuscitar o lease de um curso cancelado.

const STORAGE_KEY = 'ead_lease_state';

function loadPersistedState () {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      leases: parsed.leases || {},
      // { [enrollId]: true } — enrolls que a última lista fresca marcou sem
      // acesso ativo. Persistido pra o gate de renovação valer no boot offline.
      blockedEntitlements: parsed.blockedEntitlements || {}
    };
  } catch {
    return { leases: {}, blockedEntitlements: {} };
  }
}

function savePersistedState (state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      leases: state.leases,
      blockedEntitlements: state.blockedEntitlements
    }));
  } catch (e) {
    console.warn('[lease] Falha ao salvar state:', e?.message || e);
  }
}

const initial = loadPersistedState();

export default {
  namespaced: true,

  state: {
    // { [enrollId]: { claim: {...}, syncedAt: ms } }
    leases: initial.leases,
    // { [enrollId]: true } — enrolls que o backend indicou sem acesso ativo.
    blockedEntitlements: initial.blockedEntitlements
  },

  mutations: {
    SET_LEASE (state, { enrollId, claim }) {
      Vue.set(state.leases, String(enrollId), {
        claim,
        syncedAt: Date.now()
      });
      savePersistedState(state);
    },
    CLEAR_LEASE (state, { enrollId }) {
      Vue.delete(state.leases, String(enrollId));
      savePersistedState(state);
    },
    CLEAR_ALL (state) {
      state.leases = {};
      state.blockedEntitlements = {};
      savePersistedState(state);
    },
    SET_BLOCKED_ENTITLEMENTS (state, blockedMap) {
      state.blockedEntitlements = blockedMap || {};
      savePersistedState(state);
    },

    // Marca UM enroll, sem reconstruir o mapa. O SET_ acima é o caminho do
    // syncEntitlements, que recalcula tudo a partir da lista fresca; a revogação
    // pontual não tem lista nenhuma em mãos e não pode apagar o resto.
    SET_ENTITLEMENT_BLOCKED (state, { enrollId, blocked }) {
      const key = String(enrollId);
      if (blocked) Vue.set(state.blockedEntitlements, key, true);
      else Vue.delete(state.blockedEntitlements, key);
      savePersistedState(state);
    }
  },

  getters: {
    // Estado do lease de um enroll. Sempre devolve uma das 5 strings:
    // 'valid' | 'grace' | 'expired' | 'revoked' | 'unknown'.
    // Quando a feature está desabilitada (kill switch), retorna 'valid' pra
    // todos — playback fica sempre liberado.
    playableState: (state, getters, rootState) => (enrollId) => {
      if (!isLeaseFeatureEnabled()) return 'valid';
      const entry = state.leases[String(enrollId)];
      const claim = entry?.claim || null;
      const currentStudentId = rootState.user?.currentUser?.studentId || null;
      return evaluateClaim(claim, currentStudentId);
    },

    // Conveniência: true se o player pode tocar (valid OU grace).
    isEnrollPlayable: (state, getters) => (enrollId) => {
      const st = getters.playableState(enrollId);
      return st === 'valid' || st === 'grace';
    },

    // Dias inteiros restantes até bloqueio total (graceUntil). Negativo se já
    // passou. null se não há claim. Usado pelo banner amarelo.
    daysUntilExpiry: (state) => (enrollId) => {
      const entry = state.leases[String(enrollId)];
      return daysUntilExpiryFn(entry?.claim || null);
    },

    // Lista enrollIds que têm download e estão fora de 'valid'. Usado pela
    // página MyDownloads pra renderizar overlay de bloqueio nos cards.
    blockedEnrollIds: (state, getters, rootState) => {
      if (!isLeaseFeatureEnabled()) return [];
      const downloads = rootState.download?.downloads || {};
      const blocked = [];
      Object.keys(downloads).forEach(enrollId => {
        const st = getters.playableState(enrollId);
        if (st === 'expired' || st === 'revoked' || st === 'unknown') {
          blocked.push(String(enrollId));
        }
      });
      return blocked;
    }
  },

  actions: {
    // Renovação: emite claim novo e persiste no native + state local. Idempotente.
    // Triggers: boot online, networkStatusChange → online, qualquer 2xx em URL de enroll,
    // botão "Reconectar agora" no modal de bloqueio.
    async touch ({ state, commit, rootState }, { enrollId }) {
      if (!isLeaseFeatureEnabled()) return;
      if (!enrollId) return;

      // Entitlement: o backend não emite 402, a lista de enrolls é a fonte de
      // "tem acesso". Se o enroll está marcado bloqueado, NÃO renova — senão
      // qualquer 2xx em /enrolls/ (interceptor) ou o touch de boot
      // ressuscitaria o lease de um curso cancelado/inadimplente/desativado.
      if (state.blockedEntitlements[String(enrollId)]) return;

      const studentId = rootState.user?.currentUser?.studentId;
      if (!studentId) return;  // sem user ainda — próximo touch resolve

      const { ttlMs, graceMs } = getDefaultConfig();
      const claim = buildClaim({
        enrollId, studentId, now: Date.now(), ttlMs, graceMs
      });

      const ok = await bridgeSetLease(enrollId, claim);
      // Atualiza cache JS mesmo se bridge falhou — fallback "soft": getter vai
      // refletir o estado intencionado; scheme handler nativo vai falhar 403
      // e bloquear playback de qualquer forma. Próximo touch retenta.
      commit('SET_LEASE', { enrollId, claim });
      return ok;
    },

    // Sincroniza entitlement a partir da lista FRESCA de enrolls (GET /enrolls).
    // Fonte de verdade de "o aluno ainda tem acesso" — o backend não emite 402,
    // então a revogação vem daqui: pendingPayment / blocked / temporarilyDisabled.
    // Revoga o lease dos bloqueados (com download) e renova os liberados. Só deve
    // ser chamado com lista de REDE (não cache) — ver enroll/getUserEnroll.
    async syncEntitlements ({ commit, dispatch, rootState }, { items, total, httpStatus } = {}) {
      if (!isLeaseFeatureEnabled()) return;
      if (!Array.isArray(items)) return;

      // Separa os enrolls da lista fresca em liberados vs marcados-bloqueados.
      const liberatedIds = new Set();
      const flaggedBlocked = {};
      items.forEach(e => {
        if (!e || e.id == null) return;
        const id = String(e.id);
        if (e.pendingPayment || e.blocked || e.temporarilyDisabled) flaggedBlocked[id] = true;
        else liberatedIds.add(id);
      });

      const downloads = rootState.download?.downloads || {};
      const downloadIds = Object.keys(downloads);

      // "Ausente da lista = sem acesso": um curso baixado que NÃO aparece na
      // lista (matrícula expirada / acesso revogado, se o backend REMOVE em vez
      // de marcar) também deve ser bloqueado. GUARD: só aplica a regra de
      // ausência quando a lista veio COMPLETA. Lista truncada pela paginação
      // (Content-Range items=0-1000, aluno com mais matrículas que o teto) NÃO
      // pode revogar downloads legítimos.
      //
      // Vazio autoritativo: o backend responde 204 quando o aluno não tem
      // matrícula NENHUMA (MarketplaceController: `!data.Items.Any()`), que é o
      // sinal mais forte de acesso revogado que ele emite. O guard antigo era
      // `items.length > 0`, escrito para o caso "erro transitório 2xx" — só que
      // esse caso não existe (erro não é 2xx) e o efeito era inverter o sinal:
      // revogação total virava renovação total, +30 dias offline em curso que o
      // aluno perdeu. Um 204 é lista completa e vazia, e vale como tal.
      const authoritativeEmpty = httpStatus === 204;
      const listComplete = authoritativeEmpty || !total || items.length >= total;
      const treatAbsentAsBlocked = (authoritativeEmpty || items.length > 0) && listComplete;

      // Mapa final = marcados na lista + (se guard ok) downloads ausentes dela.
      // Precisa conter os ausentes também pra que o gate do `touch` (interceptor
      // de qualquer 2xx) não os renove de volta.
      const blockedMap = { ...flaggedBlocked };
      if (treatAbsentAsBlocked) {
        downloadIds.forEach(id => {
          const key = String(id);
          if (!liberatedIds.has(key)) blockedMap[key] = true;
        });
      }
      commit('SET_BLOCKED_ENTITLEMENTS', blockedMap);

      // Aplica só nos enrolls com download (únicos com lease relevante).
      // Bloqueado → revoga (bloqueia playback offline já). Liberado → renova
      // (a lista fresca é a melhor prova de acesso; também "des-revoga" quem
      // regularizou o pagamento, pois touch cunha claim novo sem revokedAt).
      await Promise.all(downloadIds.map(id => {
        if (blockedMap[String(id)]) {
          return dispatch('revoke', { enrollId: id, reason: 'Acesso ao curso indisponível' });
        }
        return dispatch('touch', { enrollId: id });
      }));
    },

    // Revogação imediata. Disparado pelo interceptor HTTP em resposta 402.
    // Marca revokedAt no claim atual (não cria novo) e persiste. Se não havia
    // claim, cria um "sintético" só pra carregar o revokedAt — o student vai
    // ver o modal de bloqueio mesmo num download pré-feature.
    async revoke ({ state, commit, rootState }, { enrollId, reason }) {
      if (!isLeaseFeatureEnabled()) return;
      if (!enrollId) return;

      const existing = state.leases[String(enrollId)]?.claim;
      const now = Date.now();
      let claim;
      if (existing) {
        claim = { ...existing, revokedAt: now };
        if (reason) claim.revokedReason = String(reason).slice(0, 256);
      } else {
        const studentId = rootState.user?.currentUser?.studentId || '';
        // Claim sintético: graceUntil = agora (já vencido). Garante que o
        // playableState devolve 'revoked' mesmo sem TTL prévio.
        claim = {
          v: 1,
          enrollId: String(enrollId),
          studentId: String(studentId),
          issuedAt: now,
          validUntil: now,
          graceUntil: now,
          ttlMs: 0,
          graceMs: 0,
          revokedAt: now,
          revokedReason: reason ? String(reason).slice(0, 256) : null
        };
      }

      await bridgeSetLease(enrollId, claim);
      commit('SET_LEASE', { enrollId, claim });

      // Marcar bloqueado é o que faz a revogação GRUDAR. O `touch` só é barrado
      // por este mapa — carimbar revokedAt no claim não o impede. Sem isto,
      // qualquer 2xx seguinte numa URL do enroll (o interceptor dispara touch em
      // toda resposta de sucesso) cunhava um claim novo, sem revokedAt, e
      // desfazia a revogação. Pior: em setCurrentEnroll o full-grid e o
      // last-access do mesmo enroll saem em paralelo — 402 num, 200 no outro —,
      // então quem vencia era quem respondesse por último.
      commit('SET_ENTITLEMENT_BLOCKED', { enrollId, blocked: true });
    },

    // Botão "Reconectar agora" no modal de bloqueio. Difere do touch passivo:
    // é blocking (caller espera UI loading) e força o caller a checar o
    // resultado. Retorna true se conseguiu emitir claim novo, false caso
    // contrário (offline real, user não logado, etc).
    async forceRevalidate ({ dispatch, state, rootState }, { enrollId }) {
      if (!isLeaseFeatureEnabled()) return true;
      const isConnected = rootState.dom?.isConnected !== false;
      if (!isConnected) return false;

      // Busca a lista fresca ANTES de tentar renovar. O `touch` é barrado por
      // blockedEntitlements, e quem levanta esse bloqueio é o syncEntitlements
      // disparado no getUserEnroll — ou seja, a única prova aceitável de que o
      // aluno regularizou é o backend dizer que o curso está liberado, não o
      // fato de ele ter tocado no botão. Antes daqui o botão funcionava por
      // acidente: o touch não era barrado porque a revogação não marcava o mapa.
      if (state.blockedEntitlements[String(enrollId)]) {
        // `awaitEntitlements`: o getUserEnroll solta o syncEntitlements em
        // fire-and-forget, e aqui a leitura do mapa logo abaixo depende dele ter
        // terminado.
        await dispatch('enroll/getUserEnroll', { awaitEntitlements: true }, { root: true }).catch(() => {});
        // Continua bloqueado → a lista fresca confirmou a perda de acesso.
        if (state.blockedEntitlements[String(enrollId)]) return false;
      }

      return await dispatch('touch', { enrollId });
    },

    // Apaga lease do disco + cache. Chamado no removeAllFromEnroll do
    // download.js quando o último LO do enroll é removido.
    async clearLease ({ commit }, { enrollId }) {
      if (!enrollId) return;
      await bridgeRemoveLease(enrollId);
      commit('CLEAR_LEASE', { enrollId });
    },

    // Rehidrata cache JS a partir dos arquivos nativos. Chamado no boot pra
    // sincronizar estado: localStorage pode estar fresh (DevTools clear) mas
    // o native ainda tem leases válidos. Lê tudo que estiver no índice de
    // downloads e popula state.
    async hydrateFromNative ({ commit, rootState }) {
      if (!isLeaseFeatureEnabled()) return;
      const currentStudentId = rootState.user?.currentUser?.studentId;
      const downloads = rootState.download?.downloads || {};
      const enrollIds = Object.keys(downloads);
      for (const enrollId of enrollIds) {
        const claim = await bridgeGetLease(enrollId);
        if (!claim) continue;
        // Só hidrata leases da conta logada — sem isso, num device com downloads
        // de outra conta, os leases dela voltariam pro cache JS (vazamento de
        // enrollIds/leases entre contas). Avaliam como 'expired' de qualquer
        // forma, mas não devem entrar no cache. Sem currentStudentId (boot
        // pré-user) hidrata tudo, como antes.
        if (currentStudentId && String(claim.studentId) !== String(currentStudentId)) continue;
        commit('SET_LEASE', { enrollId, claim });
      }
    },

    // Limpa tudo no logout: cache JS (mas NÃO os arquivos nativos — re-login
    // na conta original ainda funciona porque o studentId bate).
    clearCache ({ commit }) {
      commit('CLEAR_ALL');
    }
  }
};
